import { ErrorModal, useUTLSettingsContext } from "@dexteel/mesf-core";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { Box, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { SectionPanel } from "@/controls/section-panel/SectionPanel";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	getProductSizes,
	getProductTypes,
} from "@/shared/repositories/ProductRepository";
import { MillSetupDelayModal } from "./modals/MillSetupDelayModal";
import type { StandData } from "../models/StandData";
import { useCampaignData } from "../hooks/useCampaignData";
import { useCurrentDelay } from "../hooks/useCurrentDelay";
import { useFurnaceBillets } from "../hooks/useFurnaceBillets";
import { useResetTemporalBillets } from "../hooks/useResetTemporalBillets";
import { useStandsData } from "../hooks/useStandsData";
import { useUpdateCampaign } from "../hooks/useUpdateCampaign";
import { getRecipesMeta } from "../repositories/MillSetupRepository";
import { CampaignHeader } from "./CampaignHeader";
import { LooperSection } from "./LooperSection";
import { ProductionTable } from "./ProductionTable";
import { SpeedTemperatureTable } from "./SpeedTemperatureTable";
import { StandsGrid } from "./StandsGrid";
import { ChangePassModal } from "./modals/ChangePassModal";
import { ChangeProductModal } from "./modals/ChangeProductModal";
import { ChangeRollsetModal } from "./modals/ChangeRollsetModal";
import { ChangeWorkOrderModal } from "./modals/ChangeWorkOrderModal";
import { useOperationalWritesLocked } from "@/shared/hooks/useOperationalWritesLocked";

// Narrower on small screens so the stand matrices keep their room
const SIDE_COLUMN_WIDTH = "clamp(340px, 21vw, 400px)";

// Default reopen interval in seconds (same as NavbarStatusBar)
const DEFAULT_REOPEN_SECONDS = 60;

export const MillSetup = () => {
	const writesLocked = useOperationalWritesLocked();
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");

	// Get reopen interval from settings (same setting as PTB)
	const {
		state: { settings },
	} = useUTLSettingsContext();

	const reopenSeconds = useMemo(() => {
		const value = get(
			settings,
			"DELAY_POPUP_REOPEN_SECONDS",
			DEFAULT_REOPEN_SECONDS,
		);
		const parsed = Number.parseInt(String(value), 10);
		return Number.isNaN(parsed) || parsed <= 0
			? DEFAULT_REOPEN_SECONDS
			: parsed;
	}, [settings]);

	// Modal state
	const [productModalOpen, setProductModalOpen] = useState(false);
	const [workOrderModalOpen, setWorkOrderModalOpen] = useState(false);
	const [passModalStand, setPassModalStand] = useState<StandData | null>(null);
	const [rollsetModalStand, setRollsetModalStand] = useState<StandData | null>(
		null,
	);
	const [delayModalMode, setDelayModalMode] = useState<"justify" | "">("");
	const autoDelayOpened = useRef(false);

	// Cooldown: prevent auto-popup from re-opening immediately after user dismisses
	const dismissedDelayRef = useRef<{
		delayId: number;
		until: number;
	} | null>(null);

	// Queries (auto-refresh every 5s)
	const { data: campaignResult, isLoading: isCampaignLoading } =
		useCampaignData();
	const { data: stands = [], isLoading: isStandsLoading } = useStandsData();
	const { data: currentDelay } = useCurrentDelay();
	const { data: furnaceBillets = [] } = useFurnaceBillets();

	const campaign = campaignResult?.campaign ?? null;

	// Stop detection: track previous delay state to detect transitions
	const prevDelayActive = useRef<boolean | null>(null);

	useEffect(() => {
		if (!currentDelay) return;
		const isActive = currentDelay.delayActive === 1;

		if (prevDelayActive.current !== null) {
			if (!prevDelayActive.current && isActive) {
				setNotification("New Stop Detected");
			} else if (prevDelayActive.current && !isActive) {
				setNotification("The stop has ended");
			}
		}
		prevDelayActive.current = isActive;
	}, [currentDelay]);

	// Modal guard: check if ANY modal is open (#11)
	const isAnyModalOpen = useMemo(
		() =>
			productModalOpen ||
			workOrderModalOpen ||
			!!passModalStand ||
			!!rollsetModalStand ||
			delayModalMode !== "",
		[
			productModalOpen,
			workOrderModalOpen,
			passModalStand,
			rollsetModalStand,
			delayModalMode,
		],
	);

	// Auto-popup for undeclared delays (#2)
	useEffect(() => {
		if (!campaign) return;

		const isDelayActive = currentDelay?.delayActive === 1;
		const hasUndeclaredDelay =
			campaign.LastDelayId != null &&
			campaign.LastDelayCodeId == null &&
			isDelayActive;

		// Auto-open: undeclared delay + no modal open + cooldown expired.
		// Not while writes are locked: the popup exists to declare the stop.
		if (hasUndeclaredDelay && !isAnyModalOpen && !writesLocked) {
			const dismissed = dismissedDelayRef.current;
			if (
				dismissed &&
				dismissed.delayId === campaign.LastDelayId &&
				Date.now() < dismissed.until
			) {
				return; // cooldown active for this delay — don't re-open yet
			}
			setDelayModalMode("justify");
			autoDelayOpened.current = true;
			return;
		}

		// Auto-close: delay just got declared + was auto-opened
		if (
			!hasUndeclaredDelay &&
			autoDelayOpened.current &&
			delayModalMode === "justify"
		) {
			setDelayModalMode("");
			autoDelayOpened.current = false;
		}
	}, [campaign, currentDelay, isAnyModalOpen, delayModalMode, writesLocked]);

	const roughingStands = useMemo(
		() =>
			stands.filter(
				(s) =>
					s.GroupName === "Roughing Mill" || (!s.GroupName && s.Number <= 8),
			),
		[stands],
	);
	const finishingStands = useMemo(
		() =>
			stands.filter(
				(s) =>
					s.GroupName === "Finishing Mill" || (!s.GroupName && s.Number > 8),
			),
		[stands],
	);

	// Catalog queries for display names (fetch once)
	const { data: productTypes = [] } = useQuery({
		queryKey: ["productTypes"],
		queryFn: getProductTypes,
	});
	const { data: productSizes = [] } = useQuery({
		queryKey: ["productSizes"],
		queryFn: () => getProductSizes(),
	});
	const { data: recipes = [] } = useQuery({
		queryKey: ["recipesMeta"],
		queryFn: getRecipesMeta,
	});

	// Mutations
	const { updateCampaign } = useUpdateCampaign({
		onSuccess: () => setNotification("Campaign updated"),
		onError: (err) => setError(err.message),
	});

	const { resetTemporalBillets } = useResetTemporalBillets({
		onSuccess: () => setNotification("Temporary billets reset"),
		onError: (err) => setError(err.message),
	});

	// Compute product name: "ProductTypeName ProductSizeName"
	const productName = useMemo(() => {
		if (!campaign?.ProductSizeId) return "";
		const size = productSizes.find((ps) => ps.Id === campaign.ProductSizeId);
		if (!size) return "";
		const type = productTypes.find((pt) => pt.Id === size.ProductTypeId);
		const typeName = type?.Name ?? "";
		return `${typeName} ${size.Name}`.trim();
	}, [campaign?.ProductSizeId, productSizes, productTypes]);

	// Compute recipe name
	const recipeName = useMemo(() => {
		if (!campaign?.RecipeId) return "";
		const recipe = recipes.find((r) => r.Id === campaign.RecipeId);
		return recipe?.Name ?? "";
	}, [campaign?.RecipeId, recipes]);

	// Handlers
	const handleUpdateBillets = useCallback(
		(value: number) => {
			if (campaign?.Id) {
				updateCampaign({ id: campaign.Id, scheduledBillets: value });
			}
		},
		[campaign?.Id, updateCampaign],
	);

	const handleUpdateWeight = useCallback(
		(value: number) => {
			if (campaign?.Id) {
				updateCampaign({ id: campaign.Id, scheduledWeight: value });
			}
		},
		[campaign?.Id, updateCampaign],
	);

	const handleResetTemporal = useCallback(() => {
		resetTemporalBillets();
	}, [resetTemporalBillets]);

	const handleChangePass = useCallback((stand: StandData) => {
		setPassModalStand(stand);
	}, []);

	const handleChangeRollset = useCallback((stand: StandData) => {
		setRollsetModalStand(stand);
	}, []);

	const handleOpenLastStop = useCallback(() => {
		if (campaign?.LastDelayId) {
			autoDelayOpened.current = false; // manual open — don't auto-close
			setDelayModalMode("justify");
		}
	}, [campaign?.LastDelayId]);

	if (isCampaignLoading && isStandsLoading) {
		return <CenteredLazyLoading />;
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
				pb: 2,
				minHeight: "calc(100vh - 64px)",
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Mill Setup
			</Typography>
			<Paper elevation={3}>
				<CampaignHeader
					campaign={campaign}
					furnaceBillets={furnaceBillets}
					productName={productName}
					recipeName={recipeName}
					onChangeProduct={() => setProductModalOpen(true)}
					onChangeWorkOrder={() => setWorkOrderModalOpen(true)}
					onOpenLastStop={handleOpenLastStop}
					writeDisabled={writesLocked}
				/>
			</Paper>

			{/* Production, settings and loopers in a fixed column on the left; stand matrices on the right */}
			<Box
				sx={{
					flex: 1,
					display: "grid",
					gridTemplateColumns: `${SIDE_COLUMN_WIDTH} minmax(0, 1fr)`,
					// Both rows split the height evenly and never shrink below their content
					gridTemplateRows: "repeat(2, minmax(min-content, 1fr))",
					gap: 1,
				}}
			>
				<Box
					sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}
				>
					<SectionPanel title="Production" icon={<FactoryOutlinedIcon />} flush>
						<ProductionTable
							campaign={campaign}
							onUpdateBillets={handleUpdateBillets}
							onUpdateWeight={handleUpdateWeight}
							onResetTemporal={handleResetTemporal}
							writeDisabled={writesLocked}
						/>
					</SectionPanel>
					<SectionPanel
						title="Mill Settings"
						icon={<TuneOutlinedIcon />}
						flush
						sx={{ flex: 1 }}
					>
						<SpeedTemperatureTable campaign={campaign} />
					</SectionPanel>
				</Box>
				<StandsGrid
					stands={roughingStands}
					title="Roughing Mill"
					onChangePass={handleChangePass}
					onChangeRollset={handleChangeRollset}
					writeDisabled={writesLocked}
				/>
				<LooperSection stands={finishingStands} />
				<StandsGrid
					stands={finishingStands}
					title="Finishing Mill"
					onChangePass={handleChangePass}
					onChangeRollset={handleChangeRollset}
					writeDisabled={writesLocked}
				/>
			</Box>

			{/* Modals */}
			<ChangeProductModal
				open={productModalOpen}
				campaign={campaign}
				onClose={() => setProductModalOpen(false)}
				onSuccess={() => {
					setProductModalOpen(false);
					setNotification("Campaign started successfully");
				}}
				onError={(msg) => setError(msg)}
			/>

			<ChangeWorkOrderModal
				open={workOrderModalOpen}
				currentWorkOrderName={campaign?.WorkOrderName ?? ""}
				onClose={() => setWorkOrderModalOpen(false)}
				onSuccess={() => {
					setWorkOrderModalOpen(false);
					setNotification("Work order changed successfully");
				}}
				onError={(msg) => setError(msg)}
			/>

			{passModalStand && (
				<ChangePassModal
					stand={passModalStand}
					productSizeId={campaign?.ProductSizeId}
					onClose={() => setPassModalStand(null)}
					onSuccess={() => {
						setPassModalStand(null);
						setNotification("Pass changed successfully");
					}}
					onError={(msg) => setError(msg)}
				/>
			)}

			{rollsetModalStand && (
				<ChangeRollsetModal
					stand={rollsetModalStand}
					productSizeId={campaign?.ProductSizeId}
					onClose={() => setRollsetModalStand(null)}
					onSuccess={() => {
						setRollsetModalStand(null);
						setNotification("Rollset changed successfully");
					}}
					onError={(msg) => setError(msg)}
				/>
			)}

			{/* Stop Declaration Modal (with last billets + split support) */}
			<MillSetupDelayModal
				showModal={delayModalMode}
				onHide={(shouldUpdate) => {
					setDelayModalMode("");
					autoDelayOpened.current = false;
					if (shouldUpdate) {
						setNotification("Stop updated successfully");
					} else if (campaign?.LastDelayId) {
						// User closed without declaring — cooldown before re-opening
						dismissedDelayRef.current = {
							delayId: campaign.LastDelayId,
							until: Date.now() + reopenSeconds * 1000,
						};
					}
				}}
				delayId={campaign?.LastDelayId ?? null}
				delayStartTimestamp={campaign?.LastDelayStartString ?? null}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
